import { useSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import {
  DropdownMenu,
  GhostIconButton,
  MenuItem,
  MenuSection,
  showToast,
  Text,
} from "@certn/cascadia-react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ActivateApplyLinkModal } from "./ActivateApplyLinkModal";
import { DeactivateApplyLinkModal } from "./DeactivateApplyLinkModal";
import { usePermissions } from "../../../components/permissions/PermissionsProvider";

type Permission = components["schemas"]["LoggedInUserPermissionsList"];
export const applyLinkPagePermissions: Permission = {
  action: "update",
  target: "apply_link",
  scope: "all",
};

interface Props {
  isActive: boolean;
  link: string;
  id: string;
  mutate: () => void;
  name?: string;
}
export function ActionsDropdown(props: Props) {
  const {
    clientPortalApplyLinksFastFollow,
    clientPortalApplyLinksImprovements,
  } = useFlags();
  const { isActive, link, id, mutate, name } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const hasPermissionToUpdateStatus = hasPermission(applyLinkPagePermissions);

  const [showDeactivateApplyLinkModal, setShowDeactivateApplyLinkModal] =
    useState(false);

  const [showActivateApplyLinkModal, setShowActivateApplyLinkModal] =
    useState(false);

  const copyToClipBoard = () => {
    navigator.clipboard.writeText(link);
  };
  const { submit: submitDeactivate } = useSubmit(
    `/api/internal/client-portal/apply-links/${id}/deactivate/`,
    {
      showToastOnError: true,
      onSuccess: async () => {
        showToast(t("{{name}} has been deactivated.", { name }), {
          type: "success",
        });
        mutate();
      },
    }
  );

  const { submit: submitActivate } = useSubmit(
    `/api/internal/client-portal/apply-links/${id}/activate/`,
    {
      showToastOnError: true,
      onSuccess: async () => {
        showToast(t("{{name}} has been activated.", { name }), {
          type: "success",
        });
        mutate();
      },
    }
  );
  return (
    <>
      <DropdownMenu
        triggerButton={
          <GhostIconButton
            testID="apply-links-item-actions-button"
            size="small"
            icon="more_horiz"
            aria-label={t("Expand menu")}
          />
        }
        placement="bottom right"
        onItemAction={async (key) => {
          switch (key) {
            case "copy-link":
              copyToClipBoard();
              showToast(t("Copied"), {
                type: "success",
              });
              break;
            case "edit-apply-link":
              navigate(`/apply-link/${id}`);
              break;
            case "deactivate-apply-link":
              setShowDeactivateApplyLinkModal(true);
              break;
            case "activate-apply-link":
              setShowActivateApplyLinkModal(true);
              break;
          }
        }}
      >
        <MenuSection>
          {clientPortalApplyLinksFastFollow && (
            <MenuItem id="edit-apply-link" testID="edit-apply-link">
              <Text style="input/text-field">{t("Edit")}</Text>
            </MenuItem>
          )}
          <MenuItem id="copy-link" testID="copy-apply-link">
            <Text style="input/text-field">{t("Copy apply link")}</Text>
          </MenuItem>
          {isActive && hasPermissionToUpdateStatus && (
            <MenuItem id="deactivate-apply-link" testID="deactivate-apply-link">
              <>
                <Text style="input/text-field" color="text-danger">
                  {t("Deactivate")}
                </Text>
              </>
            </MenuItem>
          )}
          {!isActive &&
            hasPermissionToUpdateStatus &&
            clientPortalApplyLinksImprovements && (
              <MenuItem id="activate-apply-link" testID="activate-apply-link">
                <Text style="input/text-field">{t("Activate")}</Text>
              </MenuItem>
            )}
        </MenuSection>
      </DropdownMenu>
      <DeactivateApplyLinkModal
        isOpen={showDeactivateApplyLinkModal}
        onCancel={() => setShowDeactivateApplyLinkModal(false)}
        onConfirm={() => {
          submitDeactivate();
          setShowDeactivateApplyLinkModal(false);
        }}
        name={name || ""}
        isLoading={false}
      />
      <ActivateApplyLinkModal
        isOpen={showActivateApplyLinkModal}
        onCancel={() => setShowActivateApplyLinkModal(false)}
        onConfirm={() => {
          submitActivate();
          setShowActivateApplyLinkModal(false);
        }}
        name={name || ""}
        isLoading={false}
      />
    </>
  );
}
